import "@babylonjs/core/Meshes/thinInstanceMesh";
import { ARENA_HOME_TEAMS, ARENA_SPORT, isArenaKind, type ArenaKind, type ArenaState, type ArenaTeam } from "@townsy/contracts";
import { arenaFieldOf, arenaSeatsOf } from "@townsy/world";
import type { VenueSceneContext, VenueSceneHandle, VenueSeatPose } from "../../types";
import { asArenaState, secondsToKickoff } from "../model";
import { CHEER_SHOW_MS, buildCheerFlags, cheerHop, type CheerSpot } from "./cheers";
import { buildPlayers, type PlayersHandle } from "./players";
import { paintScoreboard, scoreboardKey } from "./scoreboard";
import { buildCrowd, buildStadium } from "./stadium";
import { ArenaTimeline } from "./timeline";
import type { Ctx } from "./paint";

const MOMENT_SHOW_MS = 3500;
const PLACEHOLDER_AWAY: ArenaTeam = { name: "Visitors", short: "VIS", primary: "#5F6773", secondary: "#F4F4F0" };

/**
 * A Sports Park stadium in 3D (ADR-0044): the bowl, the painted field and its goals, the crowd, the players and the
 * ball moving from the server's ~10 Hz frames (blended on the game's clock), the big scoreboard (and its flash for a big
 * moment), real fans sitting in their seats (sitAt; they hop when they cheer, waving a team pennant on a pole, and the
 * drawn crowd around them jumps up in a ripple: ./cheers). Only displays the server's state.
 */
export function buildArena3d(ctx: VenueSceneContext): VenueSceneHandle & { debug(): Record<string, unknown> } {
  const { scene, template } = ctx;
  const kind = template.venue;
  if (!isArenaKind(kind)) throw new Error(`not a stadium: ${String(kind)}`);
  const sport = ARENA_SPORT[kind];
  const home = ARENA_HOME_TEAMS[sport];
  let away = PLACEHOLDER_AWAY;
  const stadium = buildStadium(scene, kind, home, away.name);
  const crowd = buildCrowd(scene, kind, home, away);
  // The players are built from the first state's roster (fixed for the sport).
  let players: PlayersHandle | null = null;
  let rosterKey = "";
  ctx.addShadowCasters(stadium.casters);

  // Team pennants waved by cheering fans (a pole and a pennant per fan, thin-instanced).
  const flags = buildCheerFlags(scene);
  const field = arenaFieldOf(kind);

  const timeline = new ArenaTimeline();
  const seatsByIndex = arenaSeatsOf(kind);
  let state: ArenaState | null = null;
  let receivedAt = 0;
  let serverOffset = 0;
  let seats = new Map<string, number>();
  let screenKey = "";
  let lastScreenPaint = 0;
  let teamsKey = "";
  let lastSample = 0;
  let lastFrame = performance.now();

  const repaintScreen = (nowMs: number, force: boolean) => {
    const secs = state ? secondsToKickoff(state, receivedAt, Date.now()) : 0;
    // A big moment flashes on the screen; the final whistle shows the final score instead.
    const momentOn = Boolean(state?.moment && state.moment.kind !== "final" && Date.now() + serverOffset - state.moment.at < MOMENT_SHOW_MS);
    const key = scoreboardKey(state, secs, momentOn);
    if (key === screenKey) return;
    // The game clock runs several seconds per second: at most ~4 repaints a second.
    if (!force && nowMs - lastScreenPaint < 240) return;
    screenKey = key;
    lastScreenPaint = nowMs;
    const t = stadium.screens.texture;
    paintScoreboard(t.getContext() as unknown as Ctx, stadium.screens.width, stadium.screens.height, state, secs, momentOn);
    t.update();
  };
  repaintScreen(performance.now(), true);

  /** The fan cheering now (actor id -> team and when, by the server's clock). */
  const cheerOf = (actorId: string): { team: 0 | 1; at: number } | null => {
    const c = state?.cheers.find((x) => x.a === actorId);
    return c ? { team: c.team, at: c.at } : null;
  };

  /** The cheers being shown now: where the fan is (their seat, or where they stand facing the field), age, side. */
  const liveCheers = (): Array<{ at: CheerSpot; ageMs: number; team: 0 | 1 }> => {
    const serverNow = Date.now() + serverOffset;
    const out: Array<{ at: CheerSpot; ageMs: number; team: 0 | 1 }> = [];
    for (const c of state?.cheers ?? []) {
      // (A fresh cheer can look a few ms old in the future on this clock.)
      const ageMs = Math.max(0, serverNow - c.at);
      if (ageMs >= CHEER_SHOW_MS) continue;
      const seat = seats.get(c.a);
      const s = seat === undefined ? null : seatsByIndex[seat];
      const at: CheerSpot = s ? { x: s.x, y: s.y, z: s.z, facing: s.facing } : { x: c.x, y: 0, z: c.z, facing: Math.atan2(field.cx - c.x, field.cz - c.z) };
      out.push({ at, ageMs, team: c.team });
    }
    return out;
  };

  const batterIndex = (): number => {
    if (sport !== "baseball" || !state || state.phase !== "live") return -1;
    const f = arenaFieldOf(kind);
    if (f.sport !== "baseball") return -1;
    const battingTeam = state.board.baseball?.half === "bottom" ? 0 : 1;
    let best = -1;
    let bestD = 2.2;
    for (let i = 0; i < state.players.team.length; i++) {
      if (state.players.team[i] !== battingTeam) continue;
      const d = Math.hypot(state.pos[i * 4]! / 100 - (f.home.x - 0.95), state.pos[i * 4 + 1]! / 100 - f.home.z);
      if (d < bestD) {
        best = i;
        bestD = d;
      }
    }
    return best;
  };

  return {
    update(raw) {
      const next = asArenaState(raw);
      if (!next) return;
      const now = performance.now();
      state = next;
      receivedAt = Date.now();
      serverOffset = next.now - receivedAt;
      timeline.push(next, now);
      const rk = next.players.team.join("") + next.players.role.join(",");
      if (rk !== rosterKey) {
        rosterKey = rk;
        players?.dispose();
        players = buildPlayers(scene, sport, next.players, next.home, next.away);
        ctx.addShadowCasters(players.meshes);
        teamsKey = "";
      }
      const tk = `${next.home.name}|${next.away.name}|${next.away.primary}`;
      if (tk !== teamsKey) {
        teamsKey = tk;
        away = next.away;
        players?.setTeams(next.home, next.away);
        crowd.setTeams(next.home, next.away);
        stadium.repaintField(next.home, next.away.name);
      }
      seats = new Map(next.seats.map(([seat, actorId]) => [actorId, seat]));
      const local = ctx.localActorId();
      const mySeat = local !== null ? seats.get(local) : undefined;
      const mine = mySeat === undefined ? null : (seatsByIndex[mySeat] ?? null);
      crowd.setTaken(new Set(next.seats.map(([seat]) => seat)), mine ? { x: mine.x, z: mine.z, facing: mine.facing } : null);
      repaintScreen(now, true);
    },
    frame(dtMs, nowMs) {
      const dt = Math.min(0.1, Math.max(0, (nowMs - lastFrame) / 1000 || dtMs / 1000));
      lastFrame = nowMs;
      const sample = timeline.sample(nowMs);
      lastSample = sample ? sample.pos.length / 4 : 0;
      players?.update(sample, dt, nowMs, batterIndex());
      const cheering = liveCheers();
      crowd.animate(
        nowMs,
        state?.crowd.home ?? 0,
        state?.crowd.away ?? 0,
        cheering.map((c) => ({ x: c.at.x, z: c.at.z, ageMs: c.ageMs })),
      );
      flags.update(cheering.map((c) => ({ at: c.at, ageMs: c.ageMs, colour: (c.team === 0 ? home : away).primary })));
      repaintScreen(nowMs, false);
    },
    sitAt(actorId): VenueSeatPose | null {
      const index = seats.get(actorId);
      const seat = index === undefined ? undefined : seatsByIndex[index];
      if (!seat) return null;
      // A cheering fan jumps up from the seat.
      const c = cheerOf(actorId);
      const hop = c ? cheerHop(Date.now() + serverOffset - c.at) : 0;
      return { x: seat.x, y: seat.y + hop, z: seat.z, facing: seat.facing };
    },
    debug() {
      return {
        kind,
        sport,
        phase: state?.phase ?? null,
        gameNo: state?.gameNo ?? null,
        score: state?.board.score ?? null,
        players: players?.positions() ?? [],
        ball: players?.ballPosition() ?? null,
        drawnPlayers: lastSample,
        crowd: crowd.count(),
        seats: [...seats.entries()],
        flags: flags.count(),
        puckMarker: players?.puckMarker() ?? null,
        puckStreak: players?.puckStreak() ?? null,
        frames: timeline.size,
        meshes: scene.meshes.filter((m) => m.name.startsWith("arena")).length,
      };
    },
    dispose() {
      players?.dispose();
      crowd.dispose();
      stadium.dispose();
      flags.dispose();
    },
  };
}

export type { ArenaKind };
